import React from 'react';

/**
 * Fireplace schematic.
 *
 * Data model: user-placed object with `type: 'fireplace'` and
 * `category: { fireplace: true }`. No subtype field is currently
 * emitted, so there is a single visual variant: outer surround,
 * recessed inset opening (~6"), and a hearth strip at the floor.
 *
 * The 3D procedural renderer (web-app/src/components/3d/geometry/
 * proceduralRenderers/fireplaceRenderer.js) uses 60% width / 65%
 * height for the opening with a 5% bottom offset for the hearth.
 * We mirror those ratios in elevation so the schematic reads as the
 * same object.
 */

const INCHES_TO_METERS = 0.0254;
// Plan says ~6" inset for the recessed opening relative to surround.
// We clamp to a fraction of width/height to stay sensible at small
// or very narrow fireplaces.
const OPENING_INSET_M = 6 * INCHES_TO_METERS;
const HEARTH_STRIP_RATIO = 0.08;

export function FireplaceSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  obj: _obj = {},
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <FireplaceSideView
        depth={depth}
        height={height}
        view={view}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  const hearthH = Math.min(height * HEARTH_STRIP_RATIO, 6 * INCHES_TO_METERS);
  const hearthY = height - hearthH;

  const openingInsetX = Math.min(OPENING_INSET_M, width * 0.2);
  const openingTopY = Math.min(OPENING_INSET_M, height * 0.15);
  // Opening sits above the hearth strip, recessed `openingInsetX` from
  // each side and `openingTopY` from the top; its bottom rests on the
  // hearth so it reads like a real firebox.
  const openingX = openingInsetX;
  const openingW = width - openingInsetX * 2;
  const openingH = Math.max(0, hearthY - openingTopY);
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {openingW > 0 && openingH > 0 && (
        <rect
          x={openingX}
          y={openingTopY}
          width={openingW}
          height={openingH}
          fill={strokeColor}
          fillOpacity={0.12}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {/* Hearth strip — a single horizontal divider at hearthY. */}
      <line
        x1={0}
        y1={hearthY}
        x2={width}
        y2={hearthY}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/**
 * Side-elevation silhouette for a fireplace.
 *
 * LEFT view: the firebox front face is at x = 0, the wall behind the
 * fireplace is at x = depth. Mantel and hearth project FORWARD from
 * the front face (into negative x). The chimney chase steps back
 * toward the wall above the mantel, indicating the flue is buried in
 * the masonry. The viewBox is widened on both sides by `projection`
 * so that the LEFT projections (negative x) and the mirrored RIGHT
 * projections (past x = depth) both fit without clipping.
 */
function FireplaceSideView({ depth, height, view, strokeColor, strokeWidth }) {
  const hearthH = Math.min(height * HEARTH_STRIP_RATIO, 6 * INCHES_TO_METERS);
  const hearthY = height - hearthH;
  const hearthProjection = Math.min(6 * INCHES_TO_METERS, depth * 0.35);
  // Mantel cap: a thicker, more prominent shelf that overhangs the front.
  const mantelCapH = Math.min(2.5 * INCHES_TO_METERS, height * 0.05);
  const mantelCapY = Math.max(0, height * 0.22);
  const mantelProjection = Math.min(5 * INCHES_TO_METERS, depth * 0.3);
  // Chimney chase steps back toward the wall above the mantel — narrower
  // than the firebox below — to suggest the flue tapers into the chase.
  const chaseTopX = depth * 0.4; // chase occupies the back ~60% of depth
  const chaseTopY = 0;
  const chaseBottomY = mantelCapY;
  const projection = Math.max(hearthProjection, mantelProjection);

  const profile = (
    <g>
      {/* Body outline traced as a polygon so the chase taper, mantel
          cap, and hearth read as one continuous masonry silhouette. */}
      <path
        d={[
          `M ${depth} 0`,
          `L ${depth} ${height}`,
          `L ${-hearthProjection} ${height}`,
          `L ${-hearthProjection} ${hearthY}`,
          `L 0 ${hearthY}`,
          `L 0 ${mantelCapY + mantelCapH}`,
          `L ${-mantelProjection} ${mantelCapY + mantelCapH}`,
          `L ${-mantelProjection} ${mantelCapY}`,
          `L 0 ${mantelCapY}`,
          `L ${chaseTopX} ${chaseBottomY}`,
          `L ${chaseTopX} ${chaseTopY}`,
          'Z',
        ].join(' ')}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* Mantel-cap underline — distinguishes the cap from the column
          face when both are drawn as a single polygon. */}
      <line
        x1={-mantelProjection}
        y1={mantelCapY + mantelCapH}
        x2={chaseTopX * 0.6}
        y2={mantelCapY + mantelCapH}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );

  return (
    <svg
      viewBox={`${-projection} 0 ${depth + projection * 2} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      {view === 'right' ? (
        <g transform={`translate(${depth}, 0) scale(-1, 1)`}>{profile}</g>
      ) : (
        profile
      )}
    </svg>
  );
}

FireplaceSchematic.supportsSideViews = true;
